<template>
  <div id="source" class="source-editor">
    <div class="px-table-row">
      <h4>Source:</h4>
      <px-radio-button
        :options="sourceOptions"
        v-model="userSourceType"
        class="source-type-switch"/>
      <button v-if="files.length" class="secondary end" @click="clearFiles">Clear</button>
    </div>
    <div class="desc" v-if="error">{{error}}</div>
    <source-files v-if="sourceType === 'storage'"
                  ref="source-files"
                  v-model="files"
                  :index-path="url"
                  @foundSharedRootFolder="$emit('newTitleCandidate', $event)"/>
    <div v-if="sourceType === 'url'" class="px-table-row url-editor">
      <div class="px-table-cell url-wrapper">
        <px-input v-model="url" type="url" class="path" placeholder="Add source URL here"/>
        <span class="desc">{{sourceDescription}}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { PxAWSource, PxSourceByType, PxAWSourceVimeo } from '@pxflux/px-notation'
import vimeo from '../../../../models/utilities/vimeo'
import { readFile } from '../../../../lib/file-reader'
import PxInput from '../../../elements/UI/inputs/PxInput'
import PxRadioButton from '../../../elements/UI/inputs/PxRadioButton'
import SourceFiles from './SourceFiles'

export default {
  name: 'SourceEditor',
  props: {
    value: { type: Object, default: new PxAWSource() }
  },
  components: { SourceFiles, PxRadioButton, PxInput },
  data: () => ({
    userSourceType: '',
    sourceTabs: ['Storage', 'URL'],
    sourceOptions: [{ value: 'storage', label: 'Storage' }, { value: 'url', label: 'URL' }],
    sourceUrl: null,
    error: null,
    backupFiles: null,
    backupUrl: null
  }),

  computed: {
    source () {
      return this.value || {}
    },

    files: {
      get () {
        return this.source.files || []
      },
      async set (newValue) {
        try {
          let source
          if (!newValue) {
            source = new PxAWSource()
          } else if (newValue.files.length === 1 && newValue.files[0].file instanceof File) {
            source = await this.makeSourceFromFile(newValue.files[0].file)
          } else {
            source = PxSourceByType({
              ...this.source,
              files: newValue.files,
              local: true,
              url: newValue.indexUrl
            })
          }
          this.error = null
          this.$emit('input', source)
        } catch (e) {
          this.error = e.message
        }
      }
    },

    url: {
      get () {
        return this.source.url || ''
      },
      async set (newValue) {
        let source
        try {
          if (!newValue) {
            source = new PxAWSource()
          } else {
            source = await this.makeSourceFromUrl(newValue)
          }
          this.error = null
          this.$emit('input', source)
        } catch (e) {
          this.error = e.message
        }
      }
    },

    autoSourceType () {
      if (!this.source.files.length && !this.source.url) {
        return 'storage'
      }
      if (this.source.files.length || (this.source.url && !this.source.url.startsWith('http'))) {
        return 'storage'
      } else {
        return 'url'
      }
    },

    sourceType () {
      return this.userSourceType || this.autoSourceType
    },

    sourceDescription () {
      return ''
      // return this.channel.source ? this.channel.source.toString() : 'URL (HTML/Javascript or Vimeo video link)'
    }
  },

  watch: {
    sourceType (newValue, oldValue) {
      if (!newValue || !oldValue) return
      if (newValue === 'url') {
        this.backupFiles = { files: this.files, indexUrl: this.url }
        if (this.backupUrl) {
          this.url = this.backupUrl
          this.backupUrl = null
        } else {
          this.files = null
        }
      } else if (newValue === 'storage') {
        this.backupUrl = this.url
        this.url = null
        if (!this.files.length && this.backupFiles) {
          this.files = { ...this.backupFiles }
          this.backupFiles = null
        }
      }
    }
  },

  mounted () {
    this.userSourceType = this.autoSourceType
  },

  methods: {
    clearFiles () {
      this.$emit('input', new PxAWSource())
    },

    async makeSourceFromFile (file) {
      let sourceType
      try {
        sourceType = this.validSourceTypeFromIndexFile(file)
      } catch (e) {
        return Promise.reject(e)
      }
      let fileMeta = {}
      if (sourceType === 'video') {
        fileMeta = await readFile(file)
        console.log(`%c fromFile() %c fileMeta: `, 'background:#ffbb00;color:#000', 'color:#00aaff', fileMeta)
      }
      const o = {
        url: file.name,
        type: sourceType,
        local: true,
        files: [file],
        size: file.size
      }
      if (fileMeta.mediaWidth) {
        o.resolution = { w: fileMeta.mediaWidth, h: fileMeta.mediaHeight }
      }
      if (fileMeta.duration) {
        o.duration = fileMeta.duration
      }
      if (fileMeta.thumbnail) {
        o.thumbnail = {
          url: URL.createObjectURL(fileMeta.thumbnail),
          resolution: { w: fileMeta.mediaWidth, h: fileMeta.mediaHeight },
          file: fileMeta.thumbnail
        }
      }
      return Promise.resolve(PxSourceByType(o))
    },

    async makeSourceFromUrl (url) {
      if (!url) {
        return Promise.reject(new Error('Empty url'))
      }
      if (vimeo.isVimeoVideoUrl(url)) {
        const vimeoEmbed = await vimeo.getVimeoVideoInfo(url)
        return PxAWSourceVimeo.fromVimeoEmbed(url, vimeoEmbed)
      }
      return PxSourceByType({
        type: 'html',
        url
      })
    },

    /**
     * @param {File} file
     * @return {string}
     * @throws
     */
    validSourceTypeFromIndexFile (file) {
      // !!! DEBUG !!!
      console.log(`%c validSourceTypeFromIndexFile() %c file: `, 'background:#ffbb00;color:#000', 'color:#00aaff', file)
      if (!file.type) {
        throw new Error('Empty File Type')
      }
      const supportedTypes = {
        video: ['mp4'],
        audio: ['wav', 'mpeg', 'mp4', 'aac', 'aacp', 'flac'],
        image: ['apng', 'bmp', 'gif', 'jpeg', 'png', 'svg+xml', 'x-icon'],
        text: ['html']
      }
      const [type, subtype] = file.type.split('/')
      switch (type) {
        case 'video':
        case 'audio':
        case 'image':
          if (supportedTypes[type].includes(subtype)) {
            return type
          }
          throw new Error(`Unsupported ${type} type: ${file.type}`)
        case 'text':
          if (subtype === 'html') {
            return subtype
          }
          throw new Error(`Unsupported file type: ${file.type}`)
        default:
          throw new Error(`Unsupported file type: ${file.type}`)
      }
    }
  }
}
</script>

<style lang="scss">
  @import "../../../../scss/vars";

  .source-editor {
    .px-input-wrap.path {
      .px-input {
        background: $bg-color;
        font-size: $h3-font-size;
      }
    }
  }
</style>
